import { useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  ShieldCheck,
  Star,
  Lock,
} from "lucide-react";
import cwiLogo from "@/assets/colorado-web-impressions-logo-colorado-springs.jpg";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

export const Route = createFileRoute("/free-audit")({
  head: () => ({
    meta: [
      { title: "Get Your Free Marketing Audit | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "Free, owner-led marketing audit. Tell us your site, city and goal — Chris sends back a written audit within 1 business day. No call required.",
      },
      { property: "og:title", content: "Get Your Free Marketing Audit" },
      { property: "og:description", content: "Owner-led review of your website, Google rankings, Maps listing and social media. Free, no call required." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/free-audit" }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Free Audit", path: "/free-audit" },
      ]),
    ],
  }),
  component: FreeAuditPage,
});

const FreeAuditSchema = z.object({
  firstName: z.string().trim().min(1, "Please enter your first name").max(80),
  lastName: z.string().trim().min(1, "Please enter your last name").max(80),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z
    .string()
    .trim()
    .min(7, "Please enter a valid phone")
    .max(30)
    .regex(/^[0-9+()\-.\s]+$/, "Numbers and ()+-. only"),
  businessName: z.string().trim().min(1, "Please enter your business name").max(150),
  website: z
    .string()
    .trim()
    .min(1, "Please enter your website")
    .max(255)
    .refine(
      (v) => /^(https?:\/\/)?[^\s]+\.[^\s]+$/.test(v),
      "Please enter a valid website URL",
    ),
  service: z.string().trim().min(1, "Please enter your primary service").max(150),
  notes: z.string().trim().max(2000).optional().or(z.literal("")),
});

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessName: string;
  website: string;
  service: string;
  notes: string;
};
type FieldErrors = Partial<Record<keyof FormState, string>>;

const INITIAL: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  businessName: "",
  website: "",
  service: "",
  notes: "",
};

function FreeAuditPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [hp, setHp] = useState(""); // honeypot #1
  const [companyUrl, setCompanyUrl] = useState(""); // honeypot #2
  const formLoadedAtRef = useRef<number>(Date.now());

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const parsed = FreeAuditSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof FormState;
        if (k && !fieldErrors[k]) fieldErrors[k] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/free-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: parsed.data.firstName,
          lastName: parsed.data.lastName,
          email: parsed.data.email,
          phone: parsed.data.phone,
          businessName: parsed.data.businessName,
          websiteLink: parsed.data.website,
          service: parsed.data.service,
          notes: parsed.data.notes || "",
          website: hp,
          companyUrl,
          formLoadedAt: formLoadedAtRef.current,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        toast.error(data.error || "Something went wrong. Please try again.");
        return;
      }
      toast.success("Got it — your audit is on the way.");
      // Meta Pixel — Lead event for Facebook/Instagram Ads optimization.
      if (typeof window !== "undefined") {
        window.fbq?.("track", "Lead", {
          content_name: "Free Audit Request",
          content_category: "audit",
          currency: "USD",
          value: 1,
        });
      }
      setForm(INITIAL);
      navigate({ to: "/thank-you", search: { source: "audit" } });
    } catch {
      toast.error("Network error. Please try again or call (719) 419-3935.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">


      <main className="flex-1">
        <section className="mx-auto max-w-5xl px-5 py-12 lg:px-8 lg:py-16">
          {/* Headline */}
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-gold">
              <Sparkles />
              Free, Owner-Led Audit
            </span>
            <h1 className="mt-5 text-balance text-4xl font-black leading-[1.05] text-foreground sm:text-5xl lg:text-6xl">
              Get a Free Marketing Audit
              <br />
              <span className="text-gradient-gold">From the Owner — Not a Salesperson.</span>
            </h1>
          </div>

          {/* Intro video */}
          <div className="mx-auto mt-10 max-w-3xl">
            <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-border bg-black shadow-elegant">
              <iframe
                src="https://www.youtube.com/embed/xrW6NfcLsQ4?rel=0&modestbranding=1"
                title="Intro from Chris — Colorado Web Impressions"
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          {/* Two-column: benefits + form */}
          <div className="mx-auto mt-12 grid max-w-5xl gap-10 lg:grid-cols-[1fr_1.2fr]">
            {/* Left: what you get */}
            <div>
              <h2 className="text-2xl font-bold text-foreground">What's in your audit</h2>
              <ul className="mt-5 space-y-4">
                {[
                  "Get a real video walkthrough from Chris showing exactly what's wrong with your website, Google rankings, Maps listing, and social media — and where you're losing leads every day. No generic AI report. No fluff. Just real insights you can act on.",
                  "A diagnosis of what's actually keeping your phone from ringing",
                  "Get a second opinion — even if you're already working with an agency, freelancer, or handling it all in-house.",
                  "Specific fixes — ranked by impact, not by what we want to sell",
                  "A realistic roadmap of what to fix to get more leads.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 rounded-xl border border-border bg-muted/40 p-5">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-brand-gold text-brand-gold" />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    150+ five-star reviews
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  "Chris answered every question honestly — even told us where we didn't need to
                  spend money. That's rare."
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-brand-gold" /> Colorado-only
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Lock className="h-4 w-4 text-brand-gold" /> Your info stays private
                </span>
              </div>
            </div>

            {/* Right: form */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant lg:p-8">
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                {/* Honeypots — hidden from real users */}
                <div aria-hidden="true" className="absolute left-[-10000px] h-0 w-0 overflow-hidden">
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={hp}
                      onChange={(e) => setHp(e.target.value)}
                    />
                  </label>
                  <label>
                    Company URL
                    <input
                      type="text"
                      name="company_url"
                      tabIndex={-1}
                      autoComplete="off"
                      value={companyUrl}
                      onChange={(e) => setCompanyUrl(e.target.value)}
                    />
                  </label>
                </div>

                <h3 className="text-xl font-bold text-foreground">Request my free audit</h3>
                <p className="-mt-2 text-sm text-muted-foreground">
                  Takes about 20 seconds. No call required.
                </p>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="First name"
                    required
                    value={form.firstName}
                    onChange={(v) => update("firstName", v)}
                    autoComplete="given-name"
                    error={errors.firstName}
                  />
                  <Field
                    label="Last name"
                    required
                    value={form.lastName}
                    onChange={(v) => update("lastName", v)}
                    autoComplete="family-name"
                    error={errors.lastName}
                  />
                </div>

                <Field
                  label="Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  autoComplete="email"
                  error={errors.email}
                />

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Business name"
                    required
                    value={form.businessName}
                    onChange={(v) => update("businessName", v)}
                    autoComplete="organization"
                    error={errors.businessName}
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(v) => update("phone", v)}
                    autoComplete="tel"
                    error={errors.phone}
                  />
                </div>

                <Field
                  label="Website link"
                  type="url"
                  required
                  value={form.website}
                  onChange={(v) => update("website", v)}
                  autoComplete="url"
                  error={errors.website}
                />

                <Field
                  label="Primary service you want leads for?"
                  required
                  value={form.service}
                  onChange={(v) => update("service", v)}
                  error={errors.service}
                />

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-foreground">
                    Anything else you want Chris to know or check?
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    maxLength={2000}
                    rows={4}
                    aria-invalid={!!errors.notes}
                    className={`min-h-28 rounded-md border bg-background px-4 py-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${
                      errors.notes
                        ? "border-destructive focus:border-destructive focus:ring-destructive/30"
                        : "border-input focus:border-brand-gold focus:ring-brand-gold/30"
                    }`}
                  />
                  {errors.notes ? (
                    <p className="text-xs font-medium text-destructive" role="alert">
                      {errors.notes}
                    </p>
                  ) : null}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="group mt-2 inline-flex h-14 items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 text-sm font-bold uppercase tracking-wider text-brand-black shadow-gold transition-spring hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send My Free Audit"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <p className="text-center text-xs text-muted-foreground">
                  By submitting, you agree to be contacted about your audit. We never sell or
                  share your info.
                </p>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* Tiny footer — phone + minimal legal. No nav. */}
      <footer className="border-t border-border/40 bg-muted/30">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-3 px-5 py-5 text-sm text-muted-foreground sm:flex-row lg:px-8">
          <div className="flex items-center gap-3">
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 font-semibold text-foreground hover:text-brand-gold"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">Mon–Fri 9a–4p MT</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-foreground">
              Data Practices
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              Terms
            </Link>
            <span>© {new Date().getFullYear()} Colorado Web Impressions</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* --- small inline field helper --- */
function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  placeholder,
  autoComplete,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-foreground">
        {label}
        {required && <span className="text-brand-gold"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={!!error}
        maxLength={255}
        className={`h-12 rounded-md border bg-background px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${
          error
            ? "border-destructive focus:border-destructive focus:ring-destructive/30"
            : "border-input focus:border-brand-gold focus:ring-brand-gold/30"
        }`}
      />
      {error ? (
        <p className="text-xs font-medium text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* tiny inline sparkle icon to avoid extra import */
function Sparkles() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v3M12 18v3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M3 12h3M18 12h3M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
    </svg>
  );
}
